import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  ChevronDown,
  ChevronUp,
  Pencil,
  PlugZap,
  Settings,
  Shield,
  ShieldCheck,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Switch } from "@/components/ui/switch"

import { AdminTableToolbar } from "./admin-table-toolbar"
import { useAdminTableControls } from "../hooks/use-admin-table-controls"

export type AdminFeatureRow = {
  id: string
  name: string
  description: string
  enabled: boolean
  access_configured?: boolean
  has_config?: boolean
}

export function FeaturesTable({
  features,
  onToggleStatus,
  onEdit,
  onEditAccess,
  onEditConfig,
  isUpdating,
  disableAll,
}: {
  features: AdminFeatureRow[]
  onToggleStatus: (featureId: string, enabled: boolean) => void
  onEdit?: (featureId: string) => void
  onEditAccess?: (featureId: string) => void
  onEditConfig?: (featureId: string) => void
  isUpdating?: string
  disableAll?: boolean
}) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const sortedFeatures = useMemo(() => {
    if (features.length === 0) return []
    return [...features].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  }, [features])

  const controls = useAdminTableControls(sortedFeatures, {
    getName: (row) => row.name,
    getSearchText: (row) => [row.name, row.id, row.description].join(" "),
    isEnabled: (row) => row.enabled,
  })
  const displayFeatures = isExpanded ? controls.rows : controls.rows.slice(0, 8)
  const hasMore = controls.count > 8

  if (features.length === 0) {
    return (
      <EmptyState
        description={t("adminDashboard.features.emptyDescription", "No features configured")}
        icon={PlugZap}
        title={t("adminDashboard.features.emptyTitle", "No Features")}
      />
    )
  }

  return (
    <div className="space-y-3">
      <AdminTableToolbar bulkActions controls={controls} />
      <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
        <div className="col-span-3">{t("adminDashboard.features.name", "Name")}</div>
        <div className="col-span-5">{t("adminDashboard.features.description", "Description")}</div>
        <div className="col-span-2 text-right">
          {t("adminDashboard.access.access", { defaultValue: "Access" })}
        </div>
        <div className="col-span-2 text-right">{t("adminDashboard.features.status", "Status")}</div>
      </div>

      {displayFeatures.map((feature) => (
        <div
          key={feature.id}
          className="admin-responsive-grid-row bg-card hover:bg-accent/50 grid grid-cols-12 gap-4 rounded-lg border p-4 text-sm transition-colors"
        >
          <div className="col-span-3 flex flex-col">
            <span className="font-medium">{feature.name}</span>
            <span className="text-muted-foreground text-xs">{feature.id}</span>
          </div>
          <div className="text-muted-foreground col-span-5 flex items-start gap-3 lg:block">
            <span className="text-foreground w-24 shrink-0 text-xs font-medium lg:hidden">
              Description
            </span>
            <span>{feature.description || "—"}</span>
          </div>
          <div className="col-span-2 flex items-center justify-end gap-2">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Actions
            </span>
            {onEdit ? (
              <Button
                aria-label={t("adminDashboard.features.edit", { defaultValue: "Edit feature" })}
                disabled={disableAll}
                size="icon"
                type="button"
                variant="ghost"
                onClick={() => onEdit(feature.id)}
              >
                <Pencil className="size-4" />
              </Button>
            ) : null}
            {/* Config button - only show for features that support config */}
            {feature.has_config && onEditConfig && (
              <Button
                aria-label={t("adminDashboard.features.editConfig", { defaultValue: "Configure" })}
                disabled={disableAll}
                size="icon"
                type="button"
                variant="outline"
                onClick={() => onEditConfig(feature.id)}
              >
                <Settings className="size-4" />
              </Button>
            )}

            <Button
              aria-label={t("adminDashboard.access.access", { defaultValue: "Access" })}
              disabled={disableAll || !onEditAccess}
              size="icon"
              type="button"
              variant="outline"
              onClick={() => onEditAccess?.(feature.id)}
            >
              {feature.access_configured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
            </Button>
          </div>
          <div className="col-span-2 flex items-center justify-end">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Status
            </span>
            <Switch
              aria-label={`${feature.name} status`}
              checked={feature.enabled}
              disabled={disableAll || isUpdating === feature.id}
              onCheckedChange={(checked) => onToggleStatus(feature.id, checked)}
            />
            <span className="text-muted-foreground ml-2 text-xs">
              {feature.enabled
                ? t("adminDashboard.features.enabled", "Enabled")
                : t("adminDashboard.features.disabled", "Disabled")}
            </span>
          </div>
        </div>
      ))}

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button className="w-full" variant="outline" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 size-4" />
                {t("adminDashboard.features.showLess", "Show Less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                {t("adminDashboard.features.showMore", "Show {{count}} more", {
                  count: controls.count - 8,
                })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
